import { observer } from "mobx-react";
import React from "react";
import { Timeline, Text, ThemeIcon, Group, ActionIcon } from "@mantine/core";
import {
  IconArrowUpRight,
  IconArrowDownRight,
  IconAbacus,
} from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";
import { Cubby } from "../stores/Cubby";
import { convertSatsToFiatString } from "../helpers/conversions";

interface LedgerEntryProps {
  entry: {
    tokens: number;
    confirmedAtUnixtime: number;
    message: string | null;
    id: string;
    fee?: number;
  };
  cubby: Cubby;
}

const LedgerEntry = observer(({ entry, cubby }: LedgerEntryProps) => {
  const isSent = entry.tokens < 0;
  const iconColor = isSent ? "grey" : "blue";
  const icon = isSent ? (
    <IconArrowUpRight size={16} />
  ) : (
    <IconArrowDownRight size={16} />
  );
  const formattedTime = formatDistanceToNow(
    new Date(entry.confirmedAtUnixtime),
    { addSuffix: true }
  );
  const fiatString = convertSatsToFiatString(entry.tokens, cubby);
  const makeSatsString = (tokens) => {
    if (tokens == -1 || tokens == 1) {
      return "sat";
    }
    return "sats";
  };

  return (
    <Timeline.Item
      bullet={
        <ThemeIcon
          color={iconColor}
          variant="filled"
          radius="xl"
          style={{ cursor: "default" }}
        >
          {icon}
        </ThemeIcon>
      }
    >
      <Group
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: "8px",
          maxWidth: "100%",
          overflow: "hidden",
          padding: "0 12px", // Add padding to prevent elements from touching the screen edges
        }}
      >
        {/* Left Content */}
        <div
          style={{
            flex: "1 1 auto",
            minWidth: "0", // Ensure the content shrinks if needed
            overflow: "hidden",
          }}
        >
          <Text
            size="md"
            style={{
              wordBreak: "break-word",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              color: `var(--mantine-color-${iconColor}-text)`,
            }}
          >
            {isSent ? "Sent" : "Received"}
          </Text>
          <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {formattedTime}
          </Text>
          {entry.message && (
            <Text
              size="sm"
              c="dimmed"
              style={{
                wordBreak: "break-word",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {`"${entry.message}"`}
            </Text>
          )}
        </div>

        {/* Right Content */}
        <Group
          style={{
            flex: "0 0 auto",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: "8px",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              flex: "0 0 auto",
              textAlign: "right",
              whiteSpace: "nowrap",
            }}
          >
            <Text
              size="lg"
              style={{
                color: `var(--mantine-color-${iconColor}-text)`,
              }}
            >
              {entry.tokens.toLocaleString() +
                " " +
                makeSatsString(entry.tokens)}
            </Text>

            <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
              {fiatString}

              {entry.fee && (
                <Text size="xs" c="dimmed" span>
                  {" (fee: " + entry.fee + `)`}
                </Text>
              )}
            </Text>
          </div>
          <ActionIcon
            variant="subtle"
            aria-label="data"
            color="gray"
            style={{
              flexShrink: 0,
              variant: "subtle",
              opacity: 0.85,
              width: "24px",
              height: "24px",
              minWidth: "24px", // Ensure consistent size
            }}
          >
            <IconAbacus
              onClick={() => cubby.setCurrentlyFocusedLedgerItemId(entry.id)}
              style={{ opacity: 0.75 }}
              stroke={1.5}
            />
          </ActionIcon>
        </Group>
      </Group>
    </Timeline.Item>
  );
});

export default LedgerEntry;
